import { useEffect, useState, useCallback, useContext } from "react";
import TokuteishohyokiMain from "./TokuteishohyokiMain";
import { MapContext } from "../context/MapContext";
import FeatureDetail, { NiniBetaNote } from "./FeatureDetail";
import TohonBuyListTable from "./Tohon/TohonBuyListTable";
import ChibanPolygonDlModal, {
  CHIBAN_POLYGON_DL_SIDE_DEFAULT,
  CHIBAN_POLYGON_DL_SIDE_MIN,
  CHIBAN_POLYGON_DL_SIDE_MAX,
} from "./modal/ChibanPolygonDlModal";
import { NINI_DETAIL_MIN_ZOOM } from "../utils/MapManager";
import { EXCLUDED_PROPERTY_KEYS } from "../constants/Constant";
import { geojson as flatgeobuf } from "flatgeobuf";
import { Spinner } from "react-bootstrap";
import { union } from "@turf/union";

const LoadingSpinner = ({ isLoading }) => {
  if (!isLoading) return null;
  return (
    <div
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        backgroundColor: "rgba(0, 0, 0, 0.2)",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        zIndex: 9999,
      }}
    >
      <Spinner animation="border" variant="dark" />
    </div>
  );
};

// GeoJSONオブジェクトをファイルとしてダウンロードする
const saveGeojsonFile = (geojson, filename) => {
  const blob = new Blob([JSON.stringify(geojson)], {
    type: "application/json",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  document.body.appendChild(a);
  a.download = filename;
  a.href = url;
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

// 中心点から±half_meters の矩形bbox（経緯度）を算出する
const buildNiniBboxDeg = (lng, lat, half_meters) => {
  const dLat = half_meters / 111320;
  const dLng = half_meters / (111320 * Math.cos((lat * Math.PI) / 180));
  return [lng - dLng, lat - dLat, lng + dLng, lat + dLat];
};

// 筆の複合キーを作る（筆IDは地図ファイル間で重複するため、ファイル名と組み合わせる）
const createHudeKey = (properties) =>
  `${properties["ファイル名"]}_${properties["筆ID"]}`;

// 筆の属性から住所ベース文字列を生成する（FeatureDetail の createChibanStr と同じ結合順）
// Windows のファイル名不可文字は _ に置換する
const buildNiniAddressBase = (properties) => {
  const str = [
    properties["市区町村名"],
    properties["大字名"],
    properties["丁目名"],
    properties["小字名"],
    properties["予備名"],
    properties["地番"],
  ]
    .map((v) => v ?? "")
    .join("");
  return str.replace(/[\\/:*?"<>|]/g, "_");
};

// タイル境界で分割された同一筆の断片を1つの Feature に結合する
// properties は先頭断片のものを使う（全断片で同一のため）
// 出力する属性は属性テーブルの表示項目と揃える
const merge_hude_fragments = (fragments) => {
  const properties = Object.fromEntries(
    Object.entries(fragments[0].properties).filter(
      ([key]) => !EXCLUDED_PROPERTY_KEYS.includes(key)
    )
  );
  // 大多数の筆は断片化しておらず1件のみ
  if (fragments.length === 1) {
    return {
      type: "Feature",
      geometry: fragments[0].geometry,
      properties,
    };
  }
  try {
    // 断片同士はタイルバッファの分だけ重なっているため、union で切り口なく結合できる
    const merged = union({
      type: "FeatureCollection",
      features: fragments.map((f) => ({
        type: "Feature",
        geometry: f.geometry,
        properties: {},
      })),
    });
    if (merged) {
      return { type: "Feature", geometry: merged.geometry, properties };
    }
  } catch (err) {
    console.error("断片の結合に失敗したため MultiPolygon で出力します", err);
  }
  // フォールバック: 全断片を MultiPolygon に束ねる（欠けは生じないが切り口線が残る）
  const coordinates = [];
  fragments.forEach((f) => {
    if (f.geometry.type === "Polygon") {
      coordinates.push(f.geometry.coordinates);
    } else if (f.geometry.type === "MultiPolygon") {
      coordinates.push(...f.geometry.coordinates);
    }
  });
  return {
    type: "Feature",
    geometry: { type: "MultiPolygon", coordinates },
    properties,
  };
};

const RightSide = () => {
  const { map, selectedFeatures } = useContext(MapContext);
  const [isRightSideOpen, setIsRightSideOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  // 地番ポリゴンダウンロードの対象筆（null でダイアログ非表示）
  const [chibanPolygonDlTarget, setChibanPolygonDlTarget] = useState(null);

  const downloadGeojson = async (filename) => {
    setIsLoading(true);
    try {
      const FlatGeobufURL = `${process.env.REACT_APP_FLAT_GEO_BUF_URL}${filename}.fgb`;
      const response = await fetch(FlatGeobufURL);
      if (!response.ok) {
        throw new Error(
          `データが見つかりませんでした status:${response.status}`
        );
      }

      const retGeoJson = {
        type: "FeatureCollection",
        features: [],
      };
      const it = flatgeobuf.deserialize(
        response.body,
        undefined,
        (headerMeta) => {
          //console.log("metadata", headerMeta)
        }
      );

      for await (const feature of it) {
        retGeoJson.features.push(feature);
      }

      retGeoJson.features.sort((first, second) => {
        if (first.properties.筆ID > second.properties.筆ID) {
          return 1;
        } else if (first.properties.筆ID < second.properties.筆ID) {
          return -1;
        } else {
          return 0;
        }
      });

      saveGeojsonFile(retGeoJson, `${filename}.geojson`);
    } catch (err) {
      console.error(err);
      alert("ダウンロード中にエラーが発生しました");
    } finally {
      setIsLoading(false);
    }
  };

  // 地番ポリゴンDLボタン押下時: 実行前チェック（トグル・ZL）を行い、OKならダイアログを開く
  // NGの解消には地図操作が必要なため、ダイアログを開く前に案内する
  // サブスク判定はボタン側（FeatureDetail の disabled）で行うため、ここでは行わない
  const openChibanPolygonDl = (feature) => {
    // 任意座標の筆のみ: 表示トグルOFFでは対象レイヤーが描画されず抽出できない
    if (
      feature.__kind === "nini" &&
      map.getLayoutProperty("nini_kukaku_fill", "visibility") === "none"
    ) {
      alert("任意座標の表示をONにしてください");
      return;
    }
    // 公共・任意とも ZL15 未満は外枠表示となり詳細筆を抽出できない
    if (map.getZoom() < NINI_DETAIL_MIN_ZOOM) {
      alert("ズームレベルを上げて対象の筆を表示してください");
      return;
    }
    setChibanPolygonDlTarget(feature);
  };

  // 単筆ダウンロード: 対象筆の断片をビューポート全体から収集して1筆のGeoJSONを保存する
  // 成否を返す（false はダイアログを開いたままにする）
  const downloadChibanPolygonSingle = (feature) => {
    try {
      const targetKey = createHudeKey(feature.properties);
      // 押した筆の種別で対象レイヤーを決める（公共=hude / 任意=nini_kukaku_fill）
      const targetLayer =
        feature.__kind === "nini" ? "nini_kukaku_fill" : "hude";
      // 画面外ガードを単筆にも一貫して適用するため、保持済み geometry を使わず再クエリする
      const fragments = map
        .queryRenderedFeatures({ layers: [targetLayer] })
        .filter((f) => createHudeKey(f.properties) === targetKey);

      if (fragments.length === 0) {
        alert("地図を動かして対象の筆を表示してください");
        return false;
      }

      const retGeoJson = {
        type: "FeatureCollection",
        features: [merge_hude_fragments(fragments)],
      };
      saveGeojsonFile(
        retGeoJson,
        `${buildNiniAddressBase(feature.properties)}.geojson`
      );
      return true;
    } catch (err) {
      console.error(err);
      alert("ダウンロード中にエラーが発生しました");
      return false;
    }
  };

  // 範囲ダウンロード: 対象筆のクリック地点を中心とした矩形（1辺 sideMeters）内の筆を
  // 公共・任意の両レイヤーから抽出してGeoJSONを保存する。成否を返す（false はダイアログを開いたままにする）
  const downloadChibanPolygonRange = (feature, sideMeters) => {
    try {
      // 1辺の入力値をクランプして半辺(m)にする（数値でない場合は初期値）
      const side = Number.isFinite(sideMeters)
        ? Math.min(
            Math.max(sideMeters, CHIBAN_POLYGON_DL_SIDE_MIN),
            CHIBAN_POLYGON_DL_SIDE_MAX
          )
        : CHIBAN_POLYGON_DL_SIDE_DEFAULT;
      const lngLat = feature.__lngLat;
      const [minLng, minLat, maxLng, maxLat] = buildNiniBboxDeg(
        lngLat.lng,
        lngLat.lat,
        side / 2
      );

      // bbox 4隅を画面座標に変換し、すべて画面内に収まるか判定する
      // （getBounds() との経緯度比較は地図回転時に判定が甘くなるため使わない）
      const corners = [
        map.project([minLng, minLat]),
        map.project([minLng, maxLat]),
        map.project([maxLng, minLat]),
        map.project([maxLng, maxLat]),
      ];
      const canvas = map.getCanvas();
      const isInView = corners.every(
        (p) =>
          p.x >= 0 &&
          p.x <= canvas.clientWidth &&
          p.y >= 0 &&
          p.y <= canvas.clientHeight
      );
      if (!isInView) {
        // 画面外は queryRenderedFeatures が画面内の分だけ黙って返し、欠けた出力になるためガードする
        alert(
          "指定した範囲が地図の表示範囲外です。地図を動かすか縮小して、対象範囲全体を表示してください"
        );
        return false;
      }

      // 抽出は4隅の画面座標の外接矩形で行う（回転時は過剰包含のみで欠けは生じない）
      const xs = corners.map((p) => p.x);
      const ys = corners.map((p) => p.y);
      const features = map.queryRenderedFeatures(
        [
          [Math.min(...xs), Math.min(...ys)],
          [Math.max(...xs), Math.max(...ys)],
        ],
        { layers: ["hude", "nini_kukaku_fill"] }
      );

      // タイル境界で分割された断片を複合キーごとにグループ化する
      const hudeGroups = new Map();
      features.forEach((f) => {
        // 筆以外（外枠等）の混入ガード: 筆はファイル名・筆IDの両方を持つ
        if (!f.properties["ファイル名"] || f.properties["筆ID"] === undefined) {
          return;
        }
        // 公共と任意で複合キーが衝突しうるため、出所レイヤーで名前空間を分ける
        const hudeKey = `${f.layer.id}|${createHudeKey(f.properties)}`;
        if (!hudeGroups.has(hudeKey)) {
          hudeGroups.set(hudeKey, []);
        }
        hudeGroups.get(hudeKey).push(f);
      });

      if (hudeGroups.size === 0) {
        alert("クリック地点の周辺に出力対象のデータがありません");
        return false;
      }

      const retGeoJson = {
        type: "FeatureCollection",
        features: [...hudeGroups.values()].map(merge_hude_fragments),
      };

      retGeoJson.features.sort((first, second) => {
        if (first.properties.ファイル名 !== second.properties.ファイル名) {
          return first.properties.ファイル名 > second.properties.ファイル名
            ? 1
            : -1;
        }
        if (first.properties.筆ID > second.properties.筆ID) {
          return 1;
        } else if (first.properties.筆ID < second.properties.筆ID) {
          return -1;
        } else {
          return 0;
        }
      });

      saveGeojsonFile(
        retGeoJson,
        `${buildNiniAddressBase(feature.properties)}_周辺.geojson`
      );
      return true;
    } catch (err) {
      console.error(err);
      alert("ダウンロード中にエラーが発生しました");
      return false;
    }
  };

  // サイドバーの開閉状態に合わせて関連要素のクラスを操作する関数
  const updateSideClasses = useCallback((isOpen) => {
    document
      .querySelector(".maplibregl-ctrl-top-right")
      ?.classList[isOpen ? "add" : "remove"]("active");
    document
      .querySelector(".maplibregl-ctrl-bottom-right")
      ?.classList[isOpen ? "add" : "remove"]("active");
    document
      .querySelector(".questionnaire")
      ?.classList[isOpen ? "add" : "remove"]("act");
  }, []);

  useEffect(() => {
    // 'toggleRightSide'イベントハンドラ: サイドバーの開閉を切り替える
    const handleToggle = () => {
      setIsRightSideOpen((prev) => {
        updateSideClasses(!prev);
        return !prev;
      });
    };

    // 'openRightSide'イベントハンドラ: サイドバーが閉じている場合のみ開く
    const handleOpen = () => {
      setIsRightSideOpen((prev) => {
        if (!prev) {
          updateSideClasses(true);
          return true;
        }
        return prev; // 既に開いている場合は状態を変更しない
      });
    };

    window.addEventListener("toggleRightSide", handleToggle);
    window.addEventListener("openRightSide", handleOpen);

    // クリーンアップ関数
    return () => {
      window.removeEventListener("toggleRightSide", handleToggle);
      window.removeEventListener("openRightSide", handleOpen);
    };
  }, [updateSideClasses]);

  // 新しい地物が選択されたらサイドバーを開く
  useEffect(() => {
    if (selectedFeatures && selectedFeatures.length > 0) {
      window.dispatchEvent(new CustomEvent("openRightSide"));
    }
  }, [selectedFeatures]);

  const handleClick = () => {
    // カスタムイベントで実施
    window.dispatchEvent(new CustomEvent("toggleRightSide"));
  };

  // DLダイアログの表示用文字列（対象ラベル・デフォルトファイル名）
  const chibanPolygonDlAddressBase = chibanPolygonDlTarget
    ? buildNiniAddressBase(chibanPolygonDlTarget.properties)
    : "";

  return (
    <div className={`r-side ${isRightSideOpen ? "active" : ""}`}>
      <LoadingSpinner isLoading={isLoading} />
      <ChibanPolygonDlModal
        show={chibanPolygonDlTarget !== null}
        handleClose={() => setChibanPolygonDlTarget(null)}
        targetLabel={
          chibanPolygonDlTarget
            ? `${chibanPolygonDlAddressBase}（${chibanPolygonDlTarget.properties["ファイル名"]}）`
            : ""
        }
        singleFilename={`${chibanPolygonDlAddressBase}.geojson`}
        rangeFilename={`${chibanPolygonDlAddressBase}_周辺.geojson`}
        onExecute={(mode, sideMeters) =>
          mode === "single"
            ? downloadChibanPolygonSingle(chibanPolygonDlTarget)
            : downloadChibanPolygonRange(chibanPolygonDlTarget, sideMeters)
        }
      />

      <div
        className={`r-side-open ${isRightSideOpen ? "active" : ""}`}
        id="gsi--side--open2"
        onClick={handleClick}
      >
        OPEN
      </div>
      <div className="r-side__inner">
        {/* 任意座標の筆を選択しているときは注意書きを一覧の先頭に1回だけ表示する */}
        {selectedFeatures.some((feature) => feature.__kind === "nini") && (
          <NiniBetaNote />
        )}
        <ul className="datalist p-0">
          {selectedFeatures.map((feature, index) => (
            <FeatureDetail
              key={index}
              feature={feature}
              isMainMap={true}
              downloadGeojson={
                feature.__kind === "nini" ? undefined : downloadGeojson
              }
              openChibanPolygonDl={() => openChibanPolygonDl(feature)}
            />
          ))}
        </ul>
        <TohonBuyListTable isMainMap={true} />
        <div className="accordion w-100" id="accordion_LeftSlider">
          <button
            type="button"
            className="p-2 btn btn-link btn-sm"
            data-toggle="collapse"
            data-target="#collapseSpecifiedCommercialTransactions_leftSlider"
            aria-expanded="false"
            aria-controls="collapseSpecifiedCommercialTransactions_leftSlider"
          >
            特定商取引法に基づく表記
          </button>
          <div
            className="collapse w-100"
            id="collapseSpecifiedCommercialTransactions_leftSlider"
            data-parent="#accordion_LeftSlider"
          >
            <div className="card card-body w-100 border-1">
              <TokuteishohyokiMain />
            </div>
          </div>
        </div>

        <div className="justify-content-start" style={{ marginRight: "10px" }}>
          <a
            type="button"
            className="btn btn-link btn-sm"
            role="button"
            href="https://labs.mapple.com/mapple03/mojmap/marual_mapple_registry.pdf"
            target="_blank"
            rel="noopener noreferrer"
          >
            不動産登記情報（土地）の購入方法
          </a>
          <a href="https://youtu.be/0kV-AVe55Mo" data-lity="data-lity">
            <i
              className="fa fa-youtube-play"
              style={{
                color: " #787878",
                fontSize: "23px",
                display: "inline-block",
                verticalAlign: "middle",
              }}
              aria-hidden="true"
            ></i>
          </a>
        </div>
      </div>
    </div>
  );
};

export default RightSide;
